import React, { FC } from "react";
import { Fade } from "react-awesome-reveal";
import classNames from "classnames";
import { GatsbyImage } from "gatsby-plugin-image";
import { getGatsbyImage } from "gatsby-plugin-storyblok-image";
import {
  BlockHeader,
  HeaderLevel,
} from "@app/components/common/block-header/BlockHeader";
import { BlockBody } from "@app/components/common/block-body/BlockBody";
import { IButton, IRichText, TextType } from "@app/models/storyblok/basic";
import { DEFAULT_FADE_DURATION } from "@app/constants";
import { LinkButtonAdapter } from "../link-button-adapter/LinkButtonAdapter";
import { BenefitCard } from "./types";

type Props = {
  title: IRichText<TextType>;
  subtitle: IRichText<TextType>;
  cards: BenefitCard[];
  button?: IButton[];
};

export const BenefitCards: FC<Props> = ({ title, subtitle, cards, button }) => (
  <Fade duration={DEFAULT_FADE_DURATION} triggerOnce className="w-full">
    <div
      className={classNames(
        "mt-20 flex flex-col px-5",
        "lg:mt-25 lg:px-16",
        "xl:mt-40 xl:px-24",
        "xxl:items-center",
      )}
    >
      <div
        className={classNames(
          "mb-10 w-full max-w-container-small space-y-3",
          "md:mb-15",
          "lg:space-y-5",
          "xl:mb-25",
          "xxl:text-center",
        )}
      >
        <BlockHeader
          richText={title}
          headerLevel={HeaderLevel.H2}
          className={classNames(
            "text-medium text-primary-content",
            "lg:text-large",
            "xl:text-xx-large",
          )}
        />

        {subtitle ? (
          <div
            className={classNames(
              "text-regular text-base-400",
              "xl:text-medium-large",
            )}
          >
            <BlockBody richText={subtitle} />
          </div>
        ) : null}
      </div>
      <div
        className={classNames(
          "grid w-full max-w-container grid-cols-1 gap-y-10",
          "md:grid-cols-2 md:gap-x-10 md:gap-y-15",
          "xxl:grid-cols-3 xxl:gap-x-12 xxl:gap-y-25",
        )}
      >
        {cards.map((card) => (
          <div
            key={card._uid}
            className={classNames(
              "flex flex-col justify-between gap-3 rounded-lg border border-divider pb-6",
              "xl:pb-10",
            )}
          >
            <div
              className={classNames({
                "space-y-12 xl:space-y-16": card.icon?.filename,
              })}
            >
              <div className="relative">
                <GatsbyImage
                  image={getGatsbyImage(card.image.filename, {
                    layout: "fullWidth",
                  })}
                  alt={card.image.alt}
                  className={classNames(
                    "mb-5 aspect-[4/3] w-full rounded-t-lg",
                    "md:aspect-[16/10]",
                    "xl:mb-8",
                  )}
                />
                {card.icon?.filename ? (
                  <div
                    className={classNames(
                      "absolute -bottom-8 left-6 h-20 w-20",
                      "xl:left-9",
                    )}
                  >
                    <GatsbyImage
                      image={getGatsbyImage(card.icon.filename, {
                        layout: "fullWidth",
                      })}
                      alt={card.icon.alt}
                      className="aspect-square rounded-sm"
                    />
                  </div>
                ) : null}
              </div>
              <div
                className={classNames(
                  "relative space-y-2.5 px-6 pb-2.5",
                  "xl:px-9",
                )}
              >
                <BlockHeader
                  richText={card.title}
                  headerLevel={HeaderLevel.H3}
                  className={classNames("text-medium text-primary-content")}
                />
                <div
                  className={classNames(
                    "text-regular text-base-400 [&>p]:line-clamp-2",
                  )}
                >
                  <BlockBody richText={card.text} />
                </div>
              </div>
            </div>

            {card.button?.length ? (
              <div className={classNames("px-6", "xl:px-9")}>
                <LinkButtonAdapter data={card.button[0]} />
              </div>
            ) : null}
          </div>
        ))}
      </div>

      {button?.length ? (
        <div className={classNames("mt-10", "md:mt-15", "xl:mt-25")}>
          <LinkButtonAdapter data={button[0]} />
        </div>
      ) : null}
    </div>
  </Fade>
);
